import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Compass } from 'lucide-react'
import { discoveryApi } from '@/lib/discoveryApi'
import { guildIconUrl } from '@/lib/avatar'
import { plural } from '@/lib/guildUi'
import { Avatar } from '@/components/Avatar'
import { DesktopTitlebar } from '@/components/DesktopTitlebar'
import { DiscoveryCard } from './DiscoveryCard'
import { DiscoveryBanner } from './DiscoveryBanner'
import styles from './DiscoveryPage.module.css'

/**
 * Каталог и карточка сервера для тех, кто ещё не вошёл: klichat.ru/discovery и klichat.ru/s/<slug>.
 * Данные берём публичными ручками; чтобы вступить, предлагаем войти.
 */
export function PublicDiscovery({ page, onLogin, onBack }: { page: { kind: 'catalog' } | { kind: 'guild'; slug: string }; onLogin: () => void; onBack?: () => void }) {
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  const list = useQuery({ queryKey: ['public-discovery', search], queryFn: () => discoveryApi.list({ q: search }), enabled: page.kind === 'catalog' })
  const one = useQuery({ queryKey: ['public-guild', page.kind === 'guild' ? page.slug : ''], queryFn: () => discoveryApi.bySlug((page as { slug: string }).slug), enabled: page.kind === 'guild' })

  if (page.kind === 'guild') {
    const g = one.data
    return (
      <section className={`${styles.page} ${styles.publicPage}`}>
        <DesktopTitlebar />
        {onBack ? <button type="button" className={styles.back} onClick={onBack}>← Назад</button> : null}
        <header className={styles.hero}>
          <h1><Compass size={22} /> Кличат</h1>
        </header>
        {one.isPending ? <p className={styles.empty}>Загружаем…</p> : null}
        {one.error ? <p className={styles.empty}>Такого сервера нет в каталоге.</p> : null}
        {g ? (
          <div className={styles.publicPreview}>
            {g.banner ? <DiscoveryBanner key={g.banner} guildId={g.id} hash={g.banner} className={styles.previewBanner} /> : null}
            <div className={styles.preview}>
              <Avatar initial={g.name.slice(0, 1).toUpperCase()} color="#5865F2" size={64} src={guildIconUrl(g.id, g.icon)} />
              <div>
                <h2>{g.name}</h2>
                <p className={styles.previewDesc}>{g.description}</p>
                <p className={styles.previewMeta}>
                  {g.approximate_presence_count.toLocaleString('ru-RU')} онлайн · {g.approximate_member_count.toLocaleString('ru-RU')} {plural(g.approximate_member_count, 'участник', 'участника', 'участников')}
                </p>
                <button type="button" className={styles.join} onClick={onLogin}>Войти и вступить</button>
              </div>
            </div>
          </div>
        ) : null}
      </section>
    )
  }

  const cards = list.data?.guilds ?? []
  return (
    <section className={`${styles.page} ${styles.publicPage}`}>
      <DesktopTitlebar />
      {onBack ? <button type="button" className={styles.back} onClick={onBack}>← Назад</button> : null}
      <header className={styles.hero}>
        <h1><Compass size={22} /> Каталог серверов Кличата</h1>
        <p>Открытые сообщества. Чтобы вступить, нужен аккаунт — регистрация по номеру телефона занимает минуту.</p>
        <form
          className={styles.search}
          onSubmit={(e) => {
            e.preventDefault()
            setSearch(query.trim())
          }}
        >
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Название, тема или тег" aria-label="Поиск по каталогу" />
          <button type="submit">Найти</button>
        </form>
      </header>
      {list.isPending ? <p className={styles.empty}>Загружаем…</p> : null}
      {!list.isPending && cards.length === 0 ? <p className={styles.empty}>Пока пусто.</p> : null}
      <div className={styles.grid}>
        {cards.map(c => <DiscoveryCard key={c.guild_id} card={c} onOpen={onLogin} />)}
      </div>
      <p className={styles.empty}><button type="button" className={styles.join} onClick={onLogin}>Войти в Кличат</button></p>
    </section>
  )
}
